/**
 * Context menu — one anchored popup, rebuilt per invocation from items the
 * caller assembles based on what's under the cursor. Esc / click-away / any
 * action dismisses it.
 */

export interface ContextItem {
  label: string;
  action?: () => void;
  disabled?: boolean;
  /** Renders a thin divider ABOVE this item. */
  separator?: boolean;
  /** Small right-aligned hint (shortcut, state). */
  hint?: string;
  /** Non-interactive heading row. */
  header?: boolean;
  /** Color chip before the label (material swatches). */
  swatch?: string;
}

export function showContextMenu(clientX: number, clientY: number, items: ContextItem[]): void {
  document.querySelector('.nv-menu')?.remove();
  if (items.length === 0) return;
  const menu = document.createElement('div');
  menu.className = 'nv-menu nv-context';

  for (const item of items) {
    if (item.separator) {
      const hr = document.createElement('div');
      hr.className = 'ctx-sep';
      menu.appendChild(hr);
    }
    if (item.header) {
      const h = document.createElement('div');
      h.className = 'ctx-header';
      h.textContent = item.label;
      menu.appendChild(h);
      continue;
    }
    const btn = document.createElement('button');
    btn.disabled = !!item.disabled;
    if (item.swatch) {
      const chip = document.createElement('span');
      chip.style.cssText =
        `display:inline-block;width:11px;height:11px;border-radius:3px;margin-right:7px;` +
        `background:${item.swatch};border:1px solid rgba(255,255,255,.25);vertical-align:-1px;`;
      btn.appendChild(chip);
    }
    const label = document.createElement('span');
    label.textContent = item.label;
    btn.appendChild(label);
    if (item.hint) {
      const hint = document.createElement('span');
      hint.className = 'ctx-hint';
      hint.textContent = item.hint;
      btn.appendChild(hint);
    }
    btn.addEventListener('click', () => {
      dismiss();
      item.action?.();
    });
    menu.appendChild(btn);
  }

  document.body.appendChild(menu);
  // Keep on-screen.
  const rect = menu.getBoundingClientRect();
  menu.style.left = `${Math.min(clientX, window.innerWidth - rect.width - 8)}px`;
  menu.style.top = `${Math.min(clientY, window.innerHeight - rect.height - 8)}px`;

  const dismiss = (): void => {
    menu.remove();
    window.removeEventListener('pointerdown', onAway, true);
    window.removeEventListener('keydown', onKey, true);
  };
  const onAway = (e: PointerEvent): void => {
    if (e.target instanceof Node && menu.contains(e.target)) return;
    dismiss();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      dismiss();
    }
  };
  window.addEventListener('pointerdown', onAway, true);
  window.addEventListener('keydown', onKey, true);
}
